<script setup>
import { computed } from 'vue';
import { Select } from '@dashhudson/dashing-ui/select';
import { ChipTrigger } from '@dashhudson/dashing-ui/chip-trigger';
import { Badge } from '@dashhudson/dashing-ui/badge';
import { useDashToast as useToast } from '@dashhudson/dashing-ui/toast';
import { useCommunityInteractionStore } from '@/stores/community-interactions';
import {
  INTERACTION_UPDATE_SENTIMENT_TEXT,
  COMMUNITY_SENTIMENT_UPDATE_TOASTS,
} from '@/app/community/constants';
import { SENTIMENT_FILTER_OPTIONS } from '@/app/community/constants/communityFilters';
import keyBy from 'lodash/keyBy';
import findKey from 'lodash/findKey';
import { updateSentiment } from '@/app/community/utils/mixpanel-events';
import { useCommunityPermissions } from '@/app/community/composables/useCommunityPermissions';

const props = defineProps({
  interaction: { type: Object, default: undefined },
  hideLabel: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});

const interactionStore = useCommunityInteractionStore();
const { addToast } = useToast({ life: 5000 });

const sentimentOptions = computed(() => Object.values(SENTIMENT_FILTER_OPTIONS));

// Function to determine chip label based on selection
const getChipLabel = (selectionLabel) => (props.hideLabel ? '' : selectionLabel);
const interactionSentiment = computed(() => {
  if (!props.interaction?.sentiment) {
    return undefined;
  }
  const fieldMap = keyBy(sentimentOptions.value, 'field');
  const saved = findKey(
    props.interaction.sentiment,
    (v, k) => v && Object.prototype.hasOwnProperty.call(fieldMap, k),
  );
  return saved ? fieldMap[saved] : undefined;
});

async function handleSentimentUpdated(newSentiment) {
  const previousSentiment = interactionSentiment.value;
  const resp = await interactionStore.updateCommunityInteraction(
    props.interaction.id,
    { sentiment_override: newSentiment.value },
    true,
    props.interaction,
  );
  if (resp.data?.data) {
    const messageType = INTERACTION_UPDATE_SENTIMENT_TEXT[props.interaction.type] || 'Message';
    addToast({
      severity: 'success',
      message: `${messageType} ${COMMUNITY_SENTIMENT_UPDATE_TOASTS.SUCCESS}`,
    });
    updateSentiment(props.interaction, messageType, previousSentiment?.label, newSentiment?.label);
  } else {
    addToast({
      severity: 'error',
      message: COMMUNITY_SENTIMENT_UPDATE_TOASTS.ERROR,
    });
  }
}
</script>

<template>
  <Select
    :model-value="interactionSentiment"
    :options="sentimentOptions"
    return-object
    :disabled="disabled"
    @update:model-value="handleSentimentUpdated"
  >
    <template #default="{ shown, selectionLabel, selectedOptions }">
      <ChipTrigger
        :label="getChipLabel(selectionLabel)"
        :badge="{ severity: selectedOptions?.severity }"
        :active="shown"
        :readonly="props.disabled"
      />
    </template>
    <template #optionControl="{ option }">
      <Badge :severity="option.severity" />
    </template>
  </Select>
</template>
